import React from 'react';
import { useAppCharges, usePayCharge } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

export default function AppChargesSection({ appId, showToast, dark = false }) {
  const { data: charges = [], isLoading: loading } = useAppCharges(appId);
  const payChargeMutation = usePayCharge();

  const handlePayCharge = async (chargeId) => {
    try {
      const data = await payChargeMutation.mutateAsync(chargeId);
      window.location.href = data.checkoutUrl;
    } catch (error) {
      captureError(error, {
        action: 'handlePayCharge',
        component: 'AppChargesSection',
        extra: { appId, chargeId },
      });
      showToast(error.message || 'Failed to start payment', 'error');
    }
  };

  const getStatusBadge = (status) => {
    const badges = dark ? {
      paid: { classes: 'bg-green-500/20 border border-green-500/30 text-green-300', label: 'Paid', icon: '✓' },
      unpaid: { classes: 'bg-red-500/20 border border-red-500/30 text-red-300', label: 'Unpaid', icon: '⚠' },
      refunded: { classes: 'bg-gray-500/20 border border-gray-500/30 text-gray-300', label: 'Refunded', icon: '↩' },
    } : {
      paid: { classes: 'bg-green-100 text-green-700', label: 'Paid', icon: '✓' },
      unpaid: { classes: 'bg-red-100 text-red-700', label: 'Unpaid', icon: '⚠' },
      refunded: { classes: 'bg-gray-100 text-gray-700', label: 'Refunded', icon: '↩' },
    };
    const badge = badges[status] || badges.unpaid;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.classes}`}>
        <span>{badge.icon}</span>
        <span>{badge.label}</span>
      </span>
    );
  };

  // Styling based on theme
  const containerClasses = dark 
    ? "bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 sm:p-6 shadow-xl"
    : "bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6";
  const titleClasses = dark ? "text-white" : "text-gray-900";
  const mutedTextClasses = dark ? "text-gray-400" : "text-gray-500";
  const textClasses = dark ? "text-gray-300" : "text-gray-600";

  const unpaidCharges = charges.filter(c => c.paymentStatus === 'unpaid');
  const paidCharges = charges.filter(c => c.paymentStatus === 'paid');

  if (loading) {
    return (
      <div className={containerClasses}>
        <h2 className={`text-xl font-bold ${titleClasses} mb-4`}>💳 Charges</h2>
        <div className={`text-center py-4 ${mutedTextClasses}`}>Loading charges...</div>
      </div>
    );
  }

  if (charges.length === 0) {
    return (
      <div className={containerClasses}>
        <h2 className={`text-xl font-bold ${titleClasses} mb-4`}>💳 Charges</h2>
        <div className="text-center py-8">
          <p className={`text-sm ${mutedTextClasses}`}>No charges recorded yet.</p>
          <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'} mt-2`}>Charges will appear here when work is completed on your app.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <h2 className={`text-xl font-bold ${titleClasses} mb-4`}>💳 Charges</h2>

      {/* Unpaid Charges */}
      {unpaidCharges.length > 0 && (
        <div className="mb-6">
          <h3 className={`text-sm font-medium ${dark ? 'text-red-400' : 'text-red-700'} mb-3`}>
            Unpaid Charges ({unpaidCharges.length})
          </h3>
          <div className="space-y-3">
            {unpaidCharges.map(charge => (
              <div
                key={charge.id}
                className={dark 
                  ? "bg-red-500/10 border border-red-500/30 rounded-xl p-4"
                  : "bg-red-50 border border-red-200 rounded-lg p-4"
                }
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${dark ? 'text-white' : 'text-gray-900'} mb-1`}>{charge.description}</p>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(charge.paymentStatus)}
                      <span className={`text-xs ${mutedTextClasses}`}>
                        {new Date(charge.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="text-left sm:text-right sm:ml-4">
                    <p className={`text-lg font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>${parseFloat(charge.amount).toFixed(2)}</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePayCharge(charge.id)}
                  className={dark 
                    ? "w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-400 font-medium text-sm transition-colors"
                    : "w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm"
                  }
                >
                  Pay ${parseFloat(charge.amount).toFixed(2)}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Paid Charges */}
      {paidCharges.length > 0 && (
        <div>
          <h3 className={`text-sm font-medium ${dark ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
            Payment History ({paidCharges.length})
          </h3>
          <div className="space-y-2">
            {paidCharges.map(charge => (
              <div
                key={charge.id}
                className={dark
                  ? "flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-xl"
                  : "flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
                }
              >
                <div className="flex-1">
                  <p className={`text-sm font-medium ${dark ? 'text-white' : 'text-gray-900'}`}>{charge.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusBadge(charge.paymentStatus)}
                    {charge.paidAt && (
                      <span className={`text-xs ${mutedTextClasses}`}>
                        Paid {new Date(charge.paidAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-left sm:text-right sm:ml-4">
                  <p className={`text-sm font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>${parseFloat(charge.amount).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

